import { IconName } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import parse from 'html-react-parser';
import { Key, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import BubbleModal from '../components/BubbleModal';
import Button from '../components/Button';
import FullscreenLoader from '../components/FullscreenLoader';
import Logo from '../components/Logo';
import { flattenSchema } from '../helpers/schema';
import { FormType, ModalType, SectionType } from '../helpers/types';
import { tipsIconUrl } from '../helpers/utils';
import { useAuthContext } from '../providers/AuthProvider';
import { useConfigContext } from '../providers/ConfigProvider';
import { useNewReportContext } from '../providers/NewReportProvider';
import Summary from './Summary';
import ModalPage from '../components/ModalPage';

const ReportSection = () => {
    const { t } = useTranslation();
    const { report, section, formData, setReportData, consentApproved, setConsentApproved, applicationId, allValid, reportType, saveDraft } = useNewReportContext();
    const { reportData } = useConfigContext();
    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (report === null) {
            setReportData(params.formType ?? '', parseInt(params.id ?? ''));
        }
    });

    if (!report || !section) {
        return <FullscreenLoader />;
    }

    if (section.type === 'modal') {
        return <Modal {...section} />;
    }

    if (section.type === 'form' || section.type === 'summary') {
        return (
            <motion.div key={section.url} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="container">
                <div className="row">
                    <div className="col-lg-4 d-none d-lg-flex flex-column align-items-end py-5 sticky-sidebar" style={{ paddingRight: '64px', zIndex: 3, minHeight: '100vh' }}>
                        <Logo />
                        <div className="sidebar-navigation mt-5">
                            {report.sections
                                .filter((item: SectionType) => item.type === 'form')
                                .map((item: SectionType, index: Key) => (
                                    <SidebarSection key={index} item={item} />
                                ))}

                            {allValid && (
                                <SidebarSection
                                    item={{
                                        title: 'Oppsummering',
                                        icon: 'line-columns',
                                        short_description: '',
                                        url: 'oppsummering',
                                        type: 'form',
                                        forms: [],
                                    }}
                                />
                            )}
                        </div>
                        <a href="#" className="text-decoration-none d-flex align-items-center mt-auto" onClick={async (e) => {
                            e.preventDefault();
                            // Lagre det som er fylt ut, også når skjemaet ikke
                            // validerer ennå — «Neste» er da sperret, så dette er
                            // eneste vei ut som ikke mister dataene.
                            await saveDraft();
                            const cat = reportData?.find(r => r.type === reportType)?.category;
                            const tab = cat === 'anmodning' ? 'forespørsler' : 'rapporter';
                            navigate(`/soknad/${parseInt(params.id ?? '')}/${tab}`);
                        }}>
                            {t('end_and_continue_later')}
                            <FontAwesomeIcon icon={['fal', 'xmark']} className="text-primary ms-1" />
                        </a>
                    </div>

                    <form className="steps-right-side col-lg-8 pt-lg-5 pt-4 position-relative needs-validation" noValidate>
                        <ProgressBar />
                        {section.type === 'summary' ? <Summary section={section} formData={formData} application={report} consentApproved={consentApproved} setConsentApproved={setConsentApproved} type="report" applicationId={applicationId ?? undefined} /> : <Outlet />}

                        <Tips />
                        <Footer formData={formData} />
                    </form>
                </div>
            </motion.div>
        );
    }

    return (
        <>
            <p>{section.type}</p>
            <p>{section.title}</p>
            <p>{section.url}</p>
        </>
    );
};

const Modal = (props: ModalType) => {
    const { title, subtitle, content, button, image, video, modalType, buttons } = props;
    const { prev, next, report, section } = useNewReportContext();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Når modalen er aller første skjerm finnes ingen forrige skjerm å gå til.
    // Da skal knappen hete "Tilbake" og ta brukeren tilbake til Din side.
    const isFirstSection = report?.sections?.[0]?.url === section?.url;
    const backButton = isFirstSection ? (
        <Button title="Lukk skjema" type="linkbtn" leftIcon="arrow-left" onClick={() => navigate('/din-side')} />
    ) : (
        <Button title={t('previous_screen')} type="linkbtn" leftIcon="arrow-left" onClick={prev} />
    );

    const onClickButton = (onclick) => {

        switch(onclick?.type){
            case 'url':
                return () => window.open(onclick.value, '_blank');
            case 'back':
                return prev;
            case 'next':
                return next;
            default: 
                return next;
        }
    }

    if (modalType === 'middleSection') {
        return (
            <ModalPage modalType={modalType} showCheck={true}>
                <h2>{title}</h2>
                <p className="my-3">{parse(content)}</p>

                {video && <video className="w-100 rounded-2" src={video} controls></video>}

                <div className="text-center my-3">
                    {buttons && buttons.length > 0 ? 
                        buttons.map((btn, index) => 
                            <Button 
                                key={index} 
                                title={btn.text} 
                                type={btn.type} 
                                classes="col-auto" 
                                rightIcon={btn.icons?.right} 
                                onClick={onClickButton(btn.onclick)} />)
                    : 
                        button ? (
                            <Button title={button.text} type={button.type} classes="col-auto" rightIcon="arrow-right" onClick={next} />
                        ) :  <Button title="Gå videre" classes="col-auto" rightIcon="arrow-right" onClick={next} />
                    }
                </div>
                <div className="text-center">
                    {backButton}
                </div>
            </ModalPage>
        );
    }

    return (
            <ModalPage image={image} video={video} modalType={modalType}>
                <h2>{title}</h2>
                {subtitle && <h4 className="text-muted modal-subtitle">{subtitle}</h4>}
                <p>{parse(content)}</p>

                <div className="row justify-content-center gap-3">
                   
                    {buttons && buttons.length > 0 ? 
                        buttons.map((btn, index) => 
                            <Button 
                                key={index} 
                                title={btn.text} 
                                type={btn.type} 
                                classes="col-auto" 
                                rightIcon={btn.icons?.right} 
                                onClick={onClickButton(btn.onclick)} />)
                    : 
                        button ? (
                            <Button title={button.text} type={button.type} classes="col-auto" rightIcon="arrow-right" onClick={next} />
                        ) :  <Button title="Gå videre" classes="col-auto" rightIcon="arrow-right" onClick={next} />
                    }
                </div>
                <div className="row justify-content-center">
                    {backButton}
                </div>
            </ModalPage>
        );

    return <div>{t('404')}</div>;
};

const SidebarSection = ({ item }: { item: SectionType }) => {
    const { formData, schema, report, applicationId, checkForIncompleteSection } = useNewReportContext();
    const { title, icon, short_description, url, type } = item;
    const [circleIcon, setCircleIcon] = useState(icon ?? 'page');
    const [classess, setClassess] = useState({
        clickarea: 'clickarea-default',
    });

    const navigate = useNavigate();
    const params = useParams();

    const sectionSchema = useMemo(() => schema?.shape?.[item.url], [schema, item.url]);
    const flatSectionSchema = useMemo(() => flattenSchema(sectionSchema), [sectionSchema]);
    const flatZodSchema = useMemo(() => z.object(flatSectionSchema), [flatSectionSchema]);

    const isCurrentOrPreviousSection = (sectionUrl: string) => {
        const sectionIndex = report.sections.findIndex((s: SectionType) => s.url === sectionUrl);
        const currentSectionIndex = report.sections.findIndex((s: SectionType) => s.url === params.section);
        return sectionIndex <= currentSectionIndex;
    };

    useEffect(() => {
        const shouldUpdate = Object.keys(flatSectionSchema).length > 0 && isCurrentOrPreviousSection(url);
        if (shouldUpdate) {
            // Create a Zod object from the flattened schema
            const response = flatZodSchema.safeParse(formData);
            if (response.success) {
                if (params.section !== url) {
                    setCircleIcon('check');
                }
                setClassess({
                    clickarea: 'clickarea-completed',
                });
            } else {
                setCircleIcon(icon ?? 'page');
                setClassess({
                    clickarea: 'clickarea-default',
                });
            }
        }
    }, [formData, params.section]);

    const handleClick = async () => {
        if (!isCurrentOrPreviousSection(url)) {
            // Check if the section is in progress
            const progressUrl = await checkForIncompleteSection(url);
            if (progressUrl) {
                navigate(`/rapport/${applicationId}/${report.url}/${progressUrl}`);
            } else {
                return null;
            }
        }

        const sectionIndex = report.sections.findIndex((s: SectionType) => s.url === url);

        let canNavigate = false;

        const response = flatZodSchema.safeParse(formData);
        if (response.success) {
            canNavigate = true;
        }

        if (canNavigate) {
            // Savedraft
            if (type === 'summary') {
                navigate(`/rapport/${applicationId}/${report.url}/${url}`);
            } else {
                navigate(`/rapport/${applicationId}/${report.url}/${report.sections[sectionIndex].url}/${report.sections[sectionIndex].forms[0].url}`);
            }
        }
    };

    return (
        <div className="sidebar-navigation-item position-relative" style={{ paddingBottom: '64px' }}>
            <div className={`clickarea ${classess['clickarea']} ${params.section === url && 'clickarea-active'} d-flex justify-content-end align-items-center`} style={{ cursor: 'pointer' }} onClick={handleClick}>
                <div className="sidebar-navigation-item-content text-secondary text-end">
                    <h6 className="mb-0">{title}</h6>
                    <span className="small">{short_description}</span>
                </div>
                <div className="clickarea-circle d-flex justify-content-center align-items-center border rounded-pill ms-3 " style={{ width: '72px', aspectRatio: 1 }}>
                    <FontAwesomeIcon icon={['fal', circleIcon as IconName]} size="lg" />
                </div>
            </div>
        </div>
    );
};

const ProgressBar = () => {
    const { t } = useTranslation();
    const { report, formData, schema, form, saveDraft } = useNewReportContext();
    const { disableIllustrations } = useConfigContext();
    const [totalProgress, setTotalProgress] = useState(0);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [modalActive, setModalActive] = useState(false);
    const [saving, setSaving] = useState(false);

    const customProgress = report.custom_progress;
    const navigate = useNavigate();
    const params = useParams();

    const isPreviousSection = (sectionUrl: string) => {
        const sectionIndex = report.sections.findIndex((s: SectionType) => s.url === sectionUrl);
        const currentSectionIndex = report.sections.findIndex((s: SectionType) => s.url === params.section);
        return sectionIndex < currentSectionIndex;
    };

    const isPreviousForm = (sectionUrl: string, formUrl: string) => {
        const sectionIndex = report.sections.findIndex((s: SectionType) => s.url === sectionUrl);
        const formIndex = report.sections[sectionIndex].forms.findIndex((f: FormType) => f.url === formUrl);
        const currentFormIndex = report.sections[sectionIndex].forms.findIndex((f: FormType) => f.url === params.form);
        return formIndex <= currentFormIndex;
    };

    const updateProgress = () => {
        if (!schema) return;
        let total = 0;
        let current = 0;

        Object.keys(schema.shape).forEach((sectionKey: string) => {
            const sectionObject = schema.shape[sectionKey];
            Object.keys(sectionObject.shape).forEach((formKey: string) => {
                const formObject = sectionObject.shape[formKey];

                total++

                // Wait for the result of safeParseAsync
                const response = formObject.safeParse(formData);
                if (response.success) {
                    if(customProgress) {
                        if(isPreviousSection(sectionKey)) {
                            current++;
                        } else {
                            if(isPreviousForm(sectionKey, formKey)) {
                                current++
                            }
                        }
                    } else {
                        current++;
                    }
                }
            });
        });

        setTotalProgress(total);
        setCurrentProgress(current);
    };

    useEffect(() => {
        updateProgress();
    }, [formData, form]);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-medium" style={{ fontSize: '20px' }}>
                    {report.title}
                </span>

                <div className="d-flex aling-items-center">
                    <p className="mb-0">
                        {Math.round((currentProgress / totalProgress) * 100)}% <span className="d-none d-lg-inline">{t('progress_completed')}</span>
                    </p>
                    <div className="d-lg-none">
                        <a href="#" className="text-decoration-none " onClick={() => setModalActive(true)}>
                            <FontAwesomeIcon icon={['far', 'xmark']} size="lg" className="text-primary ms-2" />
                        </a>

                        <BubbleModal active={modalActive} setActive={() => {}}>
                            <div className="text-center">
                                <div className="display-5 mb-5">{t('end_and_continue_later_modal_title')}</div>
                                {!disableIllustrations && (
                                    <img src={import.meta.env.VITE_API_BASE_URL + 'assets/svg/intro.svg'} alt={t('end_and_continue_later_modal_title')} />
                                )}
                                <p className="mt-5 mb-4">{t('report_end_and_continue_later_modal_text')}</p>
                                <div className="d-flex justify-content-center mt-4 flex-column flex-md-row gap-3">
                                    <Button
                                        title={t('cancel')}
                                        type="outline-primary"
                                        onClick={() => {
                                            setModalActive(false);
                                        }}
                                        loading={false}
                                    />
                                    <Button
                                        title={t('close_and_continue_later')}
                                        type="primary"
                                        rightIcon="arrow-right"
                                        onClick={async () => {
                                            setSaving(true);
                                            await saveDraft();
                                            navigate('/din-side');
                                        }}
                                        loading={saving}
                                    />
                                </div>
                            </div>
                        </BubbleModal>
                    </div>
                </div>
            </div>
            <div className="progress" role="progressbar" aria-label="Basic example" aria-valuenow={(currentProgress / totalProgress) * 100} aria-valuemin={0} aria-valuemax={100}>
                <div className="progress-bar bg-success" style={{ width: (currentProgress / totalProgress) * 100 + '%' }}></div>
            </div>
        </>
    );
};

const Tips = () => {
    const { form } = useNewReportContext();

    if (form && form.tips) {
        const { icon, title, value, link } = form.tips;

        return (
            <div className="row mt-5 align-items-center">
                {tipsIconUrl(icon) && (
                    <div className="col-auto mb-4">
                        <img src={tipsIconUrl(icon)!} alt="" style={{ maxWidth: '135px' }} />
                    </div>
                )}
                <div className="col">
                    {title && <h4>{title}</h4>}
                    {value && parse(value)}
                    {link && link.title && link.url && (
                        <p>
                            <a href={link.url} target="_blank">
                                {link.title}
                            </a>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return null;
};

const Footer = ({ formData }: { formData: Record<string, string> }) => {
    const { apiCall } = useAuthContext();
    const { t } = useTranslation();
    const { prev, next, schema, section, form, saveDraft, consentApproved, reportId, applicationId, allValid, setAllValid, reportType } = useNewReportContext();
    const { reportData } = useConfigContext();
    const [valid, setValid] = useState(false);
    const [isSending, setSending] = useState(false);
    const navigate = useNavigate();

    const currentValidation = () => {
        if (form && section) {
            const sectionSchema = schema?.shape[section.url];
            const formSchema = sectionSchema?.shape[form.url];

            if (sectionSchema && formSchema) {
                const response = formSchema.safeParse(formData);
                if (response.success) {
                    setValid(true);
                } else {
                    setValid(false);
                }
            }
        }
    };

    const validateSchema = () => {
        if (schema) {
            let isValid = true;

            // Iterate through each section
            Object.entries(schema.shape).forEach(([, sectionSchema]) => {
                // Iterate through each form in the section
                Object.entries((sectionSchema as z.ZodObject<any>).shape).forEach(([, formSchema]) => {
                    // Validate the form data against its schema
                    const response = (formSchema as z.ZodTypeAny).safeParse(formData);
                    if (!response.success) {
                        isValid = false;
                    }
                });
            });

            setAllValid(isValid);
        }
    };

    const handleCompleteReport = async () => {
        setSending(true);
        try {
            const response = await apiCall({
                action: 'completeReport',
                reportId,
            });

            if (response.data.status === 200) {
                const cat = reportData?.find(r => r.type === reportType)?.category;
                const returnTab = cat === 'anmodning' ? 'forespørsler' : 'rapporter';
                navigate(`/rapport/${applicationId}/rapport-innsendt`, { state: { returnTab, applicationId } });
            }
        } catch (error) {
            console.error('Error completing application:', error);
            setSending(false);
        }
    };

    useEffect(() => {
        currentValidation();
        validateSchema();
    }, [formData]);

    useEffect(() => {
        currentValidation();
        validateSchema();
    }, [form]);

    return (
        <div className="position-fixed w-100 no-print" style={{ left: 0, bottom: 0, zIndex: 2, maxWidth: 'inherit' }}>
            <div className="container">
                <div className="row justify-content-end">
                    <div className="col-lg-8">
                        <div className={`d-flex ${section?.type === 'summary' ? 'justify-content-center justify-content-lg-end' : 'justify-content-between'} align-items-center pb-lg-5 pt-lg-4 py-3 border-top blur-plate`}>
                            {section?.type === 'summary' ? (
                                <Button title={t('send')} rightIcon="arrow-right" onClick={handleCompleteReport} disabled={!allValid || !consentApproved || isSending} />
                            ) : (
                                <>
                                    <div className="mx-4 d-flex justify-content-between w-100">
                                        <Button title={t('previous')} leftIcon="arrow-left" type="outline-primary" onClick={prev} />
                                        {valid ? (
                                            <Button
                                                title={t('next')}
                                                rightIcon="arrow-right"
                                                type="primary"
                                                onClick={async () => {
                                                    saveDraft();
                                                    next();
                                                }}
                                            />
                                        ) : (
                                            <Button title={t('next')} rightIcon="arrow-right" type="disabled" disabled onClick={undefined} />
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportSection;
